import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import i18n from '../i18n';
import nativeCms from '../data/cms.json';
import { fetchPublishedCms } from '../lib/cms-api';
import type { CmsData, Consultant, Lang, Loc } from '../types/cms';

/**
 * Deep merge `source` into `target` (objects merged, arrays/scalars replaced).
 * A source object with numeric keys is merged into an existing array by index
 * (array edits from the admin UI). Mirrors the worker-side merge so frontend
 * state behaves identically to KV.
 */
export function deepMerge<T>(target: T, source: any): T {
  if (source === undefined || source === null) return target;
  if (typeof source !== 'object' || Array.isArray(source)) return source as T;
  const out: any = { ...(target as any) };
  for (const key of Object.keys(source)) {
    const s = (source as any)[key];
    const t = (target as any)?.[key];
    if (s && typeof s === 'object' && !Array.isArray(s)) {
      if (Array.isArray(t)) {
        const arr = t.slice();
        for (const idx of Object.keys(s)) {
          const i = Number(idx);
          const cur = arr[i];
          arr[i] =
            cur && typeof cur === 'object' && !Array.isArray(cur)
              ? deepMerge(cur, s[idx])
              : s[idx];
        }
        out[key] = arr;
      } else {
        out[key] = deepMerge(t ?? {}, s);
      }
    } else {
      out[key] = s;
    }
  }
  return out;
}

/**
 * Native backup bundled at build time (refreshed from the worker by
 * `scripts/fetch-cms.mjs` during prebuild). Guarantees the site renders
 * correct content instantly, even if the CMS API is unreachable.
 */
const NATIVE = nativeCms as unknown as CmsData;

interface CmsContextValue {
  cms: CmsData;
  /** True while the live published data is being fetched. */
  loading: boolean;
  /** Current UI language code. */
  lang: Lang;
}

const CmsContext = createContext<CmsContextValue>({
  cms: NATIVE,
  loading: true,
  lang: 'tc',
});

/** Push CMS i18n trees into i18next so every t() call picks up CMS content. */
function applyI18nResources(cms: CmsData) {
  for (const lng of ['en', 'tc', 'sc'] as Lang[]) {
    const tree = cms.i18n?.[lng];
    if (tree && typeof tree === 'object') {
      i18n.addResourceBundle(lng, 'translation', tree, true, true);
    }
  }
}

export function CMSProvider({ children }: { children: ReactNode }) {
  const [cms, setCms] = useState<CmsData>(NATIVE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const published = await fetchPublishedCms();
        if (cancelled || !published || typeof published !== 'object') return;
        // Overlay published data on the native backup so missing keys never
        // blank out a section (safe-fallback pattern).
        const merged = deepMerge(NATIVE, published);
        applyI18nResources(merged);
        setCms(merged);
      } catch (err) {
        console.warn('[cms] Published data unavailable, using native backup.', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<CmsContextValue>(
    () => ({
      cms,
      loading,
      lang: (i18n.language === 'tc' || i18n.language === 'sc' ? i18n.language : 'en') as Lang,
    }),
    [cms, loading]
  );

  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>;
}

export function useCMS(): CmsContextValue {
  return useContext(CmsContext);
}

/** Resolve a Loc with graceful fallback: requested → tc → en → sc. */
export function pickLoc(loc: Loc | undefined, lang: Lang): string {
  if (!loc) return '';
  return loc[lang] || loc.tc || loc.en || loc.sc || '';
}

/** Localized consultant field with fallback chain. */
export function consultantField(c: Consultant, field: 'name' | 'title' | 'description', lang: Lang): string {
  const suffix = lang === 'tc' ? 'Tc' : lang === 'sc' ? 'Sc' : 'En';
  const value = (c as any)[`${field}${suffix}`] as string | undefined;
  return value || (c as any)[`${field}Tc`] || (c as any)[`${field}En`] || (c as any)[`${field}Sc`] || '';
}

/** Active consultants in display order. */
export function activeConsultants(cms: CmsData): Consultant[] {
  return [...(cms.consultants || [])]
    .filter((c) => c.active !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}
